import { forwardRef, useMemo, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../utils/cn.js';

export interface PasswordRule {
  label: string;
  /** Predicate that returns true when the rule is satisfied. */
  test: (value: string) => boolean;
}

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Show a rules list below the input with tick-off state. */
  rules?: PasswordRule[];
  /** Show a 4-segment strength bar above the rules list. */
  showStrength?: boolean;
  /** Toggle show/hide password with an eye icon. Default true. */
  toggleVisibility?: boolean;
  /** Additional class on the wrapping `<div className="input-group">`. */
  wrapperClassName?: string;
}

/**
 * A11y note: when wrapped in `<Field>`, `id` and `aria-describedby` are
 * injected onto this component's root and then forwarded to the underlying
 * `<input>` via the spread in the JSX below — so screen readers announce
 * on the input, not the wrapping div.
 */

/**
 * `<PasswordInput>` unifies the three password UXs in the codebase today:
 *   - Login: plain input + show/hide toggle only (login flow — no rules).
 *   - RegisterStudent: input + show/hide + rules list (registration).
 *   - AcceptInvite: input + strength bar + rules list.
 *
 * Compose via props:
 *   <PasswordInput autoComplete="current-password" />                       (login)
 *   <PasswordInput autoComplete="new-password" rules={PW_RULES} />          (register)
 *   <PasswordInput autoComplete="new-password" rules={PW_RULES} showStrength />  (invite)
 *
 * Standard rule set (from AcceptInvite):
 *   { label: '8 or more characters', test: (p) => p.length >= 8 }
 *   { label: 'A number',             test: (p) => /\d/.test(p) }
 *   { label: 'A capital letter',     test: (p) => /[A-Z]/.test(p) }
 *
 * `autocomplete="new-password"` on register, `"current-password"` on login.
 * Paste is always allowed (never block — 1Password/Bitwarden users need it).
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  {
    rules,
    showStrength,
    toggleVisibility = true,
    wrapperClassName,
    className,
    value,
    onChange,
    ...rest
  },
  ref,
) {
  const [show, setShow] = useState(false);

  // Compute rule state from current value (controlled inputs) or the DOM element (uncontrolled).
  const currentValue = (value ?? '') as string;
  const rulesState = useMemo(
    () => (rules ?? []).map((r) => ({ ...r, ok: r.test(currentValue) })),
    [rules, currentValue],
  );
  const okCount = rulesState.filter((r) => r.ok).length;

  return (
    <div className="grid gap-2">
      <div className={cn('input-group', wrapperClassName)}>
        <input
          ref={ref}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          className={cn('input', className)}
          style={{ paddingLeft: 12 }}
          {...rest}
        />
        {toggleVisibility && (
          <button
            type="button"
            className="input-addon"
            onClick={() => setShow((v) => !v)}
            aria-label={show ? 'Hide password' : 'Show password'}
            aria-pressed={show}
            style={{
              borderLeft: 0,
              borderRight: '1px solid rgb(var(--line-secondary))',
              borderRadius: '0 7px 7px 0',
              cursor: 'pointer',
            }}
          >
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>

      {showStrength && rules && rules.length > 0 && (
        <div
          className="rounded-full overflow-hidden"
          style={{ height: 6, background: 'rgb(var(--bg-tertiary))' }}
          role="progressbar"
          aria-valuenow={okCount}
          aria-valuemin={0}
          aria-valuemax={rules.length}
          aria-label="Password strength"
        >
          <i
            style={{
              display: 'block',
              height: '100%',
              width: `${(okCount / rules.length) * 100}%`,
              transition: 'width var(--dur-2, 220ms), background var(--dur-2, 220ms)',
              background:
                okCount < Math.ceil(rules.length * 0.5)
                  ? 'rgb(var(--ink-danger))'
                  : okCount < rules.length
                    ? 'rgb(var(--ink-warning))'
                    : 'rgb(var(--ink-success))',
            }}
          />
        </div>
      )}

      {rules && rules.length > 0 && (
        <ul className="grid gap-1 text-sm" style={{ color: 'rgb(var(--ink-tertiary))' }}>
          {rulesState.map((r) => (
            <li
              key={r.label}
              className="flex items-center gap-2"
              style={r.ok ? { color: 'rgb(var(--ink-success))' } : undefined}
            >
              <RuleIcon ok={r.ok} />
              {r.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

function RuleIcon({ ok }: { ok: boolean }) {
  if (ok) {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2.5 6.5L5 9L10 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.25" />
    </svg>
  );
}
